import React, { useCallback, useEffect, useMemo, useState } from 'react';
import V4Tabs from './V4Tabs';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import OffcanvasRight from '../../components/OffcanvasRight';

const DocumentsBatchesV4 = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('upload'); // upload | revectorize | import
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState({ items: [], total: 0 });
  const [detail, setDetail] = useState({ open: false, batchId: null, type: '', items: null });

  const headersAuth = useMemo(() => ({ ...getAuthHeaders() }), [getAuthHeaders]);

  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        batch_type: activeTab,
        q: query || '',
        status: statusFilter || '',
        limit: String(limit),
        offset: String(offset),
      });
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/batches?${params.toString()}`, {
        headers: headersAuth,
      });
      const data = await res.json();
      setBatches({
        items: Array.isArray(data.items) ? data.items : [],
        total: typeof data.total === 'number' ? data.total : 0,
      });
    } catch {
      setBatches({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [activeTab, headersAuth, limit, offset, query, statusFilter]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const openDetail = async (batchId, type) => {
    try {
      const params = new URLSearchParams({ batch_type: type });
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/batches/${batchId}?${params.toString()}`, {
        headers: headersAuth,
      });
      const data = await res.json();
      if (!res.ok) {
        setDetail({ open: true, batchId, type, items: { error: data?.detail || 'Failed to load batch' } });
        return;
      }
      setDetail({ open: true, batchId, type, items: data });
    } catch (e) {
      setDetail({ open: true, batchId, type, items: { error: String(e) } });
    }
  };

  const batchStatus = (row) => {
    const active = Number(row.active_count || 0);
    const failed = Number(row.failed_count || 0);
    if (active > 0) return 'active';
    if (failed > 0) return 'failed';
    return 'completed';
  };

  return (
    <div className="container-fluid py-4 px-3 px-lg-4">
      <h3 className="mb-3">Documents v4</h3>
      <V4Tabs />
      <div className="card shadow-sm">
        <div className="card-body">
          <ul className="nav nav-pills mb-3">
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'upload' ? 'active' : ''}`} onClick={() => { setActiveTab('upload'); setOffset(0); }}>
                Upload Batches
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'revectorize' ? 'active' : ''}`} onClick={() => { setActiveTab('revectorize'); setOffset(0); }}>
                Re-vectorize Batches
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'import' ? 'active' : ''}`} onClick={() => { setActiveTab('import'); setOffset(0); }}>
                Import Batches
              </button>
            </li>
          </ul>

          <div className="row g-2 align-items-end mb-3">
            <div className="col-12 col-md-6 col-lg-5">
              <label className="form-label">Search</label>
              <input
                className="form-control"
                placeholder="Search batch id or filename"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-4 col-lg-3">
              <label className="form-label">Status</label>
              <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="col-12 col-md-2 col-lg-2">
              <button className="btn btn-primary w-100" onClick={() => { setOffset(0); loadBatches(); }} disabled={loading}>
                {loading ? 'Loading…' : 'Search'}
              </button>
            </div>
          </div>

          {activeTab !== 'import' && (
            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th>Batch ID</th>
                    <th>Docs</th>
                    <th>Completed</th>
                    <th>Failed</th>
                    <th>Active</th>
                    {activeTab === 'revectorize' && <th>Scope</th>}
                    <th>Status</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(batches.items || []).map((row) => (
                    <tr key={row.batch_id}>
                      <td className="text-break">{row.batch_id}</td>
                      <td>{row.doc_count || 0}</td>
                      <td>{row.completed_count || 0}</td>
                      <td>{row.failed_count || 0}</td>
                      <td>{row.active_count || 0}</td>
                      {activeTab === 'revectorize' && <td>{row.scope || '-'}</td>}
                      <td>{batchStatus(row)}</td>
                      <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'}</td>
                      <td><button className="btn btn-sm btn-outline-primary" onClick={() => openDetail(row.batch_id, activeTab)}>View</button></td>
                    </tr>
                  ))}
                  {!batches.items?.length && (
                    <tr><td colSpan={activeTab === 'revectorize' ? 9 : 8} className="text-muted">No batches found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'import' && (
            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Filename</th>
                    <th>Status</th>
                    <th>Processed</th>
                    <th>Errors</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(batches.items || []).map((row) => (
                    <tr key={row.job_id}>
                      <td className="text-break">{row.job_id}</td>
                      <td className="text-break">{row.filename || '-'}</td>
                      <td>{row.status || '-'}</td>
                      <td>{row.processed_rows || 0}/{row.total_rows || 0}</td>
                      <td>{row.errors_count || 0}</td>
                      <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'}</td>
                      <td><button className="btn btn-sm btn-outline-primary" onClick={() => openDetail(row.job_id, 'import')}>View</button></td>
                    </tr>
                  ))}
                  {!batches.items?.length && (
                    <tr><td colSpan={7} className="text-muted">No import batches found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <OffcanvasRight
        title={detail.type === 'import' ? 'Import Batch' : 'Batch Details'}
        open={detail.open}
        onClose={() => setDetail({ open: false, batchId: null, type: '', items: null })}
        widthPct={65}
      >
        {!detail.items && <div className="text-muted">Loading…</div>}
        {detail.items?.error && <div className="text-danger">{detail.items.error}</div>}
        {detail.items?.type === 'import' && detail.items?.batch && (
          <div>
            <div className="mb-2"><strong>Job ID:</strong> {detail.items.batch.job_id}</div>
            <div className="mb-2"><strong>Status:</strong> {detail.items.batch.status}</div>
            <div className="mb-2"><strong>Rows:</strong> {detail.items.batch.processed_rows}/{detail.items.batch.total_rows}</div>
            <div className="mb-2"><strong>Errors:</strong> {detail.items.batch.errors_count}</div>
            <div className="mb-2"><strong>Updated:</strong> {detail.items.batch.updated_at ? new Date(detail.items.batch.updated_at).toLocaleString() : '-'}</div>
          </div>
        )}
        {detail.items?.items && Array.isArray(detail.items.items) && (
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Doc</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.items.map((row) => (
                  <tr key={row.id || row.job_id}>
                    <td className="text-break">{row.original_filename || row.job_id}</td>
                    <td>{row.status || '-'}</td>
                    <td>{row.progress || 0}%</td>
                    <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </OffcanvasRight>
    </div>
  );
};

export default DocumentsBatchesV4;
